import { aggregateReportsByH3 } from "./aggregation.js";
import { countShadingMaximum } from "../count-shading.js";
import {
  DEFAULT_HEX_QUANTITY_THRESHOLDS,
  HEX_QUANTITY_MINIMUM_MAXIMUM,
  nextQuantityThresholds,
  normalizeQuantityThresholds,
  validateQuantityThresholds,
} from "./quantity-styling.js";
import {
  filterHexReportsBySymptoms,
  hexSymptomSelectionKey,
} from "./symptoms.js";
import { filterHexReportsByTime } from "./time-series.js";

export const HEX_QUANTITY_REFERENCE_RESOLUTION = 4;
export const HEX_QUANTITY_MAXIMUM_INCREMENT = 25;

export function quantitySliderMaximumForCells(cells) {
  if (!Array.isArray(cells)) throw new TypeError("Expected an array of H3 cells.");
  const sickCounts = cells.map((cell) => {
    const sickCount = cell?.sickCount;
    if (!Number.isInteger(sickCount) || sickCount < 0) {
      throw new TypeError("H3 cell sickCount values must be non-negative whole numbers.");
    }
    return sickCount;
  });
  return countShadingMaximum(
    sickCounts,
    HEX_QUANTITY_MINIMUM_MAXIMUM,
    HEX_QUANTITY_MAXIMUM_INCREMENT,
  );
}

export function humanQuantityScaleKey({
  datasetVersion,
  timeState = null,
  selectedSymptoms = [],
}) {
  if (typeof datasetVersion !== "string" || datasetVersion.length === 0) {
    throw new TypeError("A non-empty human-report dataset version is required.");
  }
  const startDate = timeState?.startDate || "all";
  const selectedDate = timeState?.selectedDate || "all";
  return JSON.stringify([
    datasetVersion,
    startDate,
    selectedDate,
    hexSymptomSelectionKey(selectedSymptoms),
  ]);
}

export function createHexQuantityScale({
  aggregateReports = aggregateReportsByH3,
  filterByTime = filterHexReportsByTime,
  filterBySymptoms = filterHexReportsBySymptoms,
  initialThresholds = DEFAULT_HEX_QUANTITY_THRESHOLDS,
} = {}) {
  let thresholds = validateQuantityThresholds(initialThresholds);
  let maximum = Math.max(
    HEX_QUANTITY_MINIMUM_MAXIMUM,
    Math.ceil(thresholds.upperCount / HEX_QUANTITY_MAXIMUM_INCREMENT)
      * HEX_QUANTITY_MAXIMUM_INCREMENT,
  );
  let cachedKey = null;

  const currentState = () => Object.freeze({
    maximum,
    thresholds: Object.freeze({ ...thresholds }),
  });

  return {
    get state() {
      return currentState();
    },
    refresh({
      reports,
      datasetVersion,
      timeState = null,
      selectedSymptoms = [],
    }) {
      if (!Array.isArray(reports)) throw new TypeError("Expected an array of human reports.");
      const key = humanQuantityScaleKey({ datasetVersion, timeState, selectedSymptoms });
      if (key === cachedKey) {
        return Object.freeze({
          recalculated: false,
          maximumChanged: false,
          thresholdsChanged: false,
          state: currentState(),
        });
      }

      const timeFilteredReports = timeState ? filterByTime(reports, timeState) : reports;
      const filteredReports = filterBySymptoms(timeFilteredReports, selectedSymptoms);
      const cells = aggregateReports(filteredReports, HEX_QUANTITY_REFERENCE_RESOLUTION);
      const nextMaximum = quantitySliderMaximumForCells(cells);
      const nextThresholds = normalizeQuantityThresholds(thresholds, nextMaximum);
      const maximumChanged = nextMaximum !== maximum;
      const thresholdsChanged = (
        nextThresholds.lowerCount !== thresholds.lowerCount
        || nextThresholds.upperCount !== thresholds.upperCount
      );

      maximum = nextMaximum;
      thresholds = nextThresholds;
      cachedKey = key;
      return Object.freeze({
        recalculated: true,
        maximumChanged,
        thresholdsChanged,
        state: currentState(),
      });
    },
    setThreshold(name, value) {
      const nextThresholds = nextQuantityThresholds(name, value, thresholds, maximum);
      const changed = (
        nextThresholds.lowerCount !== thresholds.lowerCount
        || nextThresholds.upperCount !== thresholds.upperCount
      );
      thresholds = nextThresholds;
      return Object.freeze({ changed, state: currentState() });
    },
  };
}
